/**
 * PartitionExplainer — explainer for the PARTITION Structural Duality Principle (Pa).
 *
 * PARTITION is the level-2 SDP that pairs with SEPARATE along a
 * parameterized self-dual adjunction. It certifies that boundary
 * discipline generalizes across scales — a SEPARATE witness can be
 * stacked across nested boundaries without producing a forbidden
 * pattern. With SEPARATE (primal) it closes as the ICG-Pa Safety
 * Envelope.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  CalloutBox,
  ExplainerCard,
  GradedAdjunctionDiagram,
  SdpLevelBadge,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { COMPOSITIONALITY_PRINCIPLE_COLOR, THEOREM_COLOR } from "../../design-system/tokens.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.PARTITION.color;
const LEVEL = COMPOSITIONALITY_PRINCIPLE_COLOR.PARTITION.level;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-partition",
  kind: `SSM · Compositionality Principle (S2 · level ${LEVEL})`,
  title: "PARTITION",
  subtitle: "Pa · parameterized self-dual · Neutralization Theorem: S",
  accent: ACCENT,
  accentAlt: THEOREM_COLOR.SEPARATE,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "SEPARATE (S, primal Neutralization Theorem)",
    href: "#explainer-separate",
    accent: THEOREM_COLOR.SEPARATE,
  },
  {
    label: "ICG-Pa (Safety Envelope)",
    href: "#explainer-icg-pa",
    accent: THEOREM_COLOR.SEPARATE,
  },
  { label: "Double category", href: "#explainer-double-category" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>PARTITION</strong> is the safety story for nesting boundaries: it certifies that "data
      and instructions never swap" holds at every scale — by a parameterized self-dual duality, not
      by inspection.
    </p>
  ),
  intuition: (
    <>
      <p>
        Once you can write "this boundary keeps data and instructions apart" (a SEPARATE witness),
        you immediately want to split or merge boundaries — a subsystem inside a subsystem.
        PARTITION is the certificate that says splitting and recombining boundaries is safe: the
        duality is self-dual, so the discipline is identical at every scale.
      </p>
      <p>
        Where SEPARATE delivers the guarantee inside one boundary, PARTITION delivers
        compositionality across nested boundaries. Together they close as the ICG-Pa Safety
        Envelope.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        PARTITION is the level-2 Structural Duality Principle whose duality is a{" "}
        <strong>parameterized self-dual</strong> adjunction: boundary splitting is left adjoint to
        recombination, and the construction is its own dual under reparameterization.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        <strong>1-categorical seam vs SDP graded adjunction</strong> (per{" "}
        <code>theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md</code>). At the 1-categorical level
        the SEPARATE seam declares an <em>absolute empty hom-set</em>: the data→instruction 2-cell
        either exists or it does not, in the strict binary sense. As a <strong>level-2</strong> SDP,
        PARTITION operates through a 2-categorical <em>graded</em> self-dual adjunction indexed by
        scale: its unit supplies, at each grade, the exact authorized type conversion a bridge must
        produce to split or merge a boundary — structurally inexpressible without the witness,
        expressible with it. Level-2 SDPs come from higher-categorical adjunctions for singleton
        Neutralization Theorems (here, S alone).
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        PARTITION is what lets the ICG-Pa Safety Envelope reduce the audit surface from "a SEPARATE
        witness per boundary" to "one envelope" carrying the self-dual adjunction unit and counit.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If a boundary is split but a sub-boundary is not a
        SEPARATE witness, PARTITION rules it out structurally — no graded-adjunction unit certifies
        the split. A boundary that confuses data and instruction at any scale is{" "}
        <em>structurally inexpressible</em> at the SDP level.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-Pa" accent={ACCENT}>
        SEPARATE (Neutralization Theorem) ⊗ PARTITION (SDP) = ICG-Pa, the single two-sided
        certificate. Audit surface collapses from{" "}
        <em>(Neutralization Theorem witness) + (SDP witness)</em> to <em>one envelope</em> carrying
        the parameterized self-dual unit and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — SDPs and ICGs (§7, §7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "Seam adjunction / bridge Kan theory",
      path: "theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md",
      note: "Authoritative treatment of the empty-hom-set seam vs the graded SDP adjunction; AGENTS.md routes here.",
    },
    {
      label: "SEPARATE kernel module",
      path: "agda/Kernel/SEPARATE/Neutralization.agda",
    },
  ],
};

const DIAGRAM = (
  <>
    <div style={{ marginBottom: "0.5rem" }}>
      <SdpLevelBadge level={LEVEL} accent={ACCENT} />
    </div>
    <GradedAdjunctionDiagram
      accent={ACCENT}
      grade="s"
      ariaLabel="PARTITION graded adjunction: boundary splitting ⊣ recombination, a parameterized self-dual indexed by scale s"
    />
  </>
);

export function PartitionExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
